import { JSONContent } from '@tiptap/core';
import ImageRepository from '@app/repositories/imageRepository';
import MapRepository from '@app/premiumFeatures/maps/mapRepository';
import EntityRepository from '@app/repositories/entityRepository';
import CommentRepository from '@app/repositories/commentRepository';
import store from '@app/store';
import emitter from 'tiny-emitter/instance';
import {
  EntityType, Universe, Relationship, GalleryItem, EntityComment,
} from '@app/models';

// In some cases the API does not return all the entity data
export class AbridgedEntity {
  id: string;

  universeId: string;

  name: string;

  galleryItems: GalleryItem[];

  entityTypeId: string;

  tags: string[];

  textFieldSummaries: Record<string, string | null>;

  constructor(obj: any) {
    this.id = obj.id;
    this.universeId = obj.universeId;
    this.name = obj.name; this.entityTypeId = obj.entityTypeId;
    if (obj.galleryItems) {
      this.galleryItems = obj.galleryItems.map((galleryItem: any) => new GalleryItem(galleryItem));
    }
    else {
      this.galleryItems = [];
    }
    this.tags = obj.tags;
    this.textFieldSummaries = obj.textFieldSummaries || {};
  }

  getThumbnailUrl(entityType: EntityType): string {
    const galleryItem = this.galleryItems[0] || entityType.galleryItem;

    if (galleryItem) {
      return `${COW_USER_IMAGE_URL}/thumbnails/${galleryItem.id}.${galleryItem.thumbnailExtension}`;
    }

    return '/images/default.jpg';
  }

  getThumbnailUrlByUniverse(universe: Universe): string {
    if (this.galleryItems.length > 0) {
      return `${COW_USER_IMAGE_URL}/thumbnails/${this.galleryItems[0].id}.${this.galleryItems[0].thumbnailExtension}`;
    }

    const galleryItem = universe.getEntityType(this.entityTypeId)?.galleryItem;

    if (galleryItem) {
      return `${COW_USER_IMAGE_URL}/thumbnails/${galleryItem.id}.${galleryItem.thumbnailExtension}`;
    }

    return '/images/default.jpg';
  }

  getImageUrl(entityType: EntityType): string {
    if (this.galleryItems.length > 0) {
      return `${COW_USER_IMAGE_URL}/${this.galleryItems[0].id}.${this.galleryItems[0].extension}`;
    }

    if (entityType.galleryItem) {
      return `${COW_USER_IMAGE_URL}/${entityType.galleryItem.id}.${entityType.galleryItem.extension}`;
    }

    return '/images/default.jpg';
  }

  getImageUrls(): string[] {
    const urls: string[] = [];
    this.galleryItems.forEach((galleryItem: GalleryItem) => {
      urls.push(`${COW_USER_IMAGE_URL}/${galleryItem.id}.${galleryItem.extension}`);
    });

    return urls;
  }
}

export default class Entity extends AbridgedEntity {
  textFields: Record<string, object>;

  relationshipFields: Record<string, Array<Relationship>>;

  constructor(obj: any) {
    super(obj);
    if (obj.textFields) {
      this.textFields = obj.textFields;
    }
    else {
      this.textFields = {};
    }

    // Conversion from plain object is a little tricky here
    // The properties of obj.relationshipFields the ids of each relationship field as strings
    // The values of these properties are arrays of raw relationship objects
    if (obj.relationshipFields) {
      this.relationshipFields = Object.fromEntries(
        Object.entries(obj.relationshipFields).map(
          ([fieldId, relationshipsObj]) => [fieldId, (relationshipsObj as any).map(
            (relationship: any) => new Relationship(relationship),
          )],
        ),
      );
    }
    else {
      this.relationshipFields = {};
    }
  }

  async rename(newName: string): Promise<Entity> {
    this.name = newName;
    return EntityRepository.update(
      {
        name: this.name,
        id: this.id,
      },
    );
  }

  async setTextField(fieldId: string, document: object): Promise<{document: object}> {
    const textFieldValue: { document: any } = await EntityRepository.createOrUpdateTextFieldValue(
      this.id,
      fieldId,
      { document: document },
    );
    this.textFields[fieldId] = textFieldValue.document;
    return { document: textFieldValue.document };
  }

  async addImage(file: File): Promise<void> {
    return ImageRepository.addEntityImage(this.universeId, this.id, file).then((image) => {
      this.galleryItems.push(image);
    });
  }

  async addImageByUrl(url: string): Promise<void> {
    return ImageRepository.addEntityImageByUrl(this.universeId, this.id, url).then((image) => {
      this.galleryItems.push(image);
    });
  }

  async reorderImages(ids: string[]): Promise<void> {
    await ImageRepository.reorderEntityImages(this.id, ids);
  }

  async addTag(tag: string): Promise<Entity> {
    this.tags.push(tag);
    return EntityRepository.update(
      {
        tags: this.tags,
        id: this.id,
      },
    );
  }

  async setTags(tags: string[]): Promise<Entity> {
    this.tags = tags;
    return EntityRepository.update(
      {
        tags: tags,
        id: this.id,
      },
    );
  }

  async delete(): Promise<void> {
    return EntityRepository.delete(this.id).then(() => {
      store.state.toasts.push(
        {
          componentName: 'UndoDeleteToast',
          key: this.id,
          message: `Deleted ${this.name}`,
          undoDelete: async() => {
            await EntityRepository.undoDelete(this.id);
            emitter.emit('undoDeleteEntity', this);
          },
        },
      );
    });
  }

  async getComments(): Promise<Array<EntityComment>> {
    return CommentRepository.list(this.id);
  }

  async addComment(document: JSONContent) {
    return CommentRepository.create(this.id, document);
  }

  async deleteImage(image: GalleryItem): Promise<void> {
    const index = this.galleryItems.findIndex((candidateImage) => candidateImage.id === image.id);
    this.galleryItems.splice(index, 1);

    const deleteCall = image.type === 'map'
      ? MapRepository.deleteMap(image.id)
      : ImageRepository.deleteEntityImage(this.id, image.id);

    return deleteCall.then(() => {
      store.state.toasts.push(
        {
          componentName: 'UndoDeleteToast',
          key: image.id,
          message: `Deleted image for "${this.name}"`,
          undoDelete: async() => {
            if (image.type === 'map') {
              await MapRepository.undoDeleteMap(image.id);
            }
            else {
              await ImageRepository.undoDeleteEntityImage(this.id, image.id);
            }

            let insertIndex = 0;
            for (const existingImage of this.galleryItems) {
              if (existingImage.position > image.position) {
                break;
              }
              insertIndex += 1;
            }

            this.galleryItems.splice(insertIndex, 0, image);
          },
        },
      );
    });
  }

  addGalleryItem(galleryItem: GalleryItem) {
    this.galleryItems.push(galleryItem);
  }

  async deleteRelationship(relationship: Relationship, index: number) {
    await EntityRepository.deleteRelationship(
      relationship.id,
    );
    this.relationshipFields[relationship.fieldId].splice(index, 1);
    store.state.toasts.push(
      {
        componentName: 'UndoDeleteToast',
        key: relationship.id,
        message: `Deleted relationship from "${this.name}" to "${relationship.to.name}"`,
        undoDelete: async() => {
          await EntityRepository.undoDeleteRelationship(relationship.id);

          this.relationshipFields[relationship.fieldId].splice(index, 0, relationship);
        },
      },
    );
  }

  async addRelationship(fieldId: string, otherEntityId: string): Promise<void> {
    return EntityRepository.createRelationship(this.id, fieldId, otherEntityId).then(
      (newRelationship) => {
        if (this.relationshipFields[fieldId] === undefined) {
          this.relationshipFields[fieldId] = [];
        }
        let inserted = false;
        const newRelationshipName = newRelationship.to.name.toLowerCase();
        for (let i = 0; i < this.relationshipFields[fieldId].length; i += 1) {
          if (newRelationshipName < this.relationshipFields[fieldId][i].to.name.toLowerCase()) {
            this.relationshipFields[fieldId].splice(i, 0, newRelationship);
            inserted = true;
            break;
          }
        }
        if (!inserted) {
          this.relationshipFields[fieldId].push(newRelationship);
        }
      },
    );
  }
}
